import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Phone, MapPin, Sparkles, Heart, GraduationCap, BookOpen, Check, X, Save, ArrowRight, ArrowLeft, PartyPopper, Rocket } from "lucide-react";
import SkillsEditor from "@/components/SkillsEditor";
import { MentorProfileEditor, MenteeSkillsEditor } from "@/components/Mentorship";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_RE = /^[0-9 +()\-]{0,20}$/;
const LOCATIONS = ["București", "Cluj"];

// Profile completion = 5 weighted checks (20% each)
export function profileCompletion(emp) {
  const checks = [
    { key: "contact", label: "Contact (telefon + locație)", done: !!(emp?.phone) && !!(emp?.location) },
    { key: "skills", label: "Competențe", done: (emp?.skills || []).length > 0 },
    { key: "hobbies", label: "Hobby & interese", done: (emp?.hobbies || []).length > 0 },
    { key: "mentor", label: "Profil mentor", done: !!emp?.mentor_active && (emp?.mentor_skills || []).length > 0 },
    { key: "mentee", label: "Ce vrei să înveți", done: (emp?.mentee_skills || []).length > 0 },
  ];
  const done = checks.filter((c) => c.done).length;
  return { pct: Math.round((done / checks.length) * 100), done, total: checks.length, checks };
}

function ContactStep({ employee, onSaved, onSkip }) {
  const [phone, setPhone] = useState(employee.phone || "");
  const [location, setLocation] = useState(employee.location || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const v = phone.trim();
    if (v && !PHONE_RE.test(v)) { toast.error("Format telefon invalid"); return; }
    setSaving(true);
    try {
      let updated;
      const r1 = await axios.patch(`${API}/employees/${employee.id}/phone`, { phone: v });
      updated = r1.data;
      const r2 = await axios.patch(`${API}/employees/${employee.id}/location`, { location });
      updated = r2.data;
      toast.success("Date de contact salvate");
      onSaved(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="wizard-step-contact" className="space-y-4">
      <div>
        <label className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 flex items-center gap-1.5">
          <Phone className="h-3 w-3" /> Telefon
        </label>
        <input
          data-testid="wizard-phone-input"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="0712 345 678"
          className="w-full h-10 px-3 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100"
        />
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 flex items-center gap-1.5">
          <MapPin className="h-3 w-3" /> Locație
        </label>
        <div className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-zinc-50 dark:bg-zinc-900">
          <button type="button" onClick={() => setLocation("")} className={`px-3 py-1 text-xs rounded-full ${location === "" ? "bg-white dark:bg-zinc-700 shadow-sm" : "text-zinc-500"}`}>—</button>
          {LOCATIONS.map((l) => (
            <button key={l} type="button" data-testid={`wizard-loc-${l}`} onClick={() => setLocation(l)} className={`px-3 py-1 text-xs rounded-full ${location === l ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "text-zinc-500"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button data-testid="wizard-contact-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90 disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "Se salvează..." : "Salvează și continuă"}
        </button>
        <button data-testid="wizard-contact-skip" onClick={onSkip} className="inline-flex items-center px-4 h-9 rounded-lg border border-border text-sm">Mai târziu</button>
      </div>
    </div>
  );
}

function HobbiesStep({ employee, onSaved, onSkip }) {
  const [draft, setDraft] = useState(employee.hobbies || []);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const add = (val) => {
    const v = (val || "").trim().replace(/\s+/g, " ").slice(0, 40);
    if (!v) return;
    setDraft((p) => (p.some((h) => h.toLowerCase() === v.toLowerCase()) ? p : [...p, v]));
    setInput("");
  };
  const save = async () => {
    let finalH = draft;
    const pending = input.trim().replace(/\s+/g, " ").slice(0, 40);
    if (pending && !finalH.some((h) => h.toLowerCase() === pending.toLowerCase())) finalH = [...finalH, pending];
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/employees/${employee.id}/hobby`, { hobbies: finalH });
      toast.success("Hobby-uri salvate");
      onSaved(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="wizard-step-hobbies" className="space-y-3">
      <div className="flex flex-wrap gap-1.5 min-h-[1.5rem]">
        {draft.length === 0 && <span className="text-[11px] text-zinc-400 italic">Niciun hobby încă.</span>}
        {draft.map((h) => (
          <span key={h} data-testid={`wizard-hobby-pill-${h}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs font-medium rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
            {h}
            <button onClick={() => setDraft((p) => p.filter((x) => x !== h))} className="ml-0.5 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-black/10 dark:hover:bg-white/10"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <input
        data-testid="wizard-hobby-input"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(input); } }}
        maxLength={40}
        placeholder="Scrie un hobby și apasă Enter (ex: Drumeții montane)"
        className="w-full h-9 px-3 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100"
      />
      <div className="flex items-center gap-2">
        <button data-testid="wizard-hobby-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90 disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "Se salvează..." : "Salvează și continuă"}
        </button>
        <button data-testid="wizard-hobby-skip" onClick={onSkip} className="inline-flex items-center px-4 h-9 rounded-lg border border-border text-sm">Mai târziu</button>
      </div>
    </div>
  );
}

export default function ProfileWizard({ open, onClose, employee, allSkills, onEmployeeUpdated }) {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open, employee?.id]);

  const completion = useMemo(() => profileCompletion(employee), [employee]);

  if (!employee) return null;

  const handleSaved = (updated) => {
    onEmployeeUpdated?.(updated);
    next();
  };
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const STEPS = [
    {
      key: "intro", icon: Rocket, title: "Hai să-ți completăm profilul",
      render: () => (
        <div data-testid="wizard-step-intro" className="space-y-4">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Un profil complet îi ajută pe colegi să te găsească după competențe, să afle ce te pasionează și să te contacteze pentru mentorat. Durează ~1 minut.
          </p>
          <ul className="space-y-1.5">
            {completion.checks.map((c) => (
              <li key={c.key} data-testid={`wizard-check-${c.key}`} className="flex items-center gap-2 text-sm">
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${c.done ? "bg-emerald-500 text-white" : "bg-zinc-200 dark:bg-zinc-700 text-zinc-400"}`}>
                  {c.done ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                </span>
                <span className={c.done ? "text-zinc-400 line-through" : "text-zinc-700 dark:text-zinc-300"}>{c.label}</span>
              </li>
            ))}
          </ul>
          <button data-testid="wizard-start-btn" onClick={next} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90">
            Începe <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    {
      key: "contact", icon: Phone, title: "Date de contact",
      render: () => <ContactStep employee={employee} onSaved={handleSaved} onSkip={next} />,
    },
    {
      key: "skills", icon: Sparkles, title: "Competențele tale",
      render: () => (
        <SkillsEditor employee={employee} allSkills={allSkills || []} onSaved={handleSaved} onCancel={next} />
      ),
    },
    {
      key: "hobbies", icon: Heart, title: "Hobby & interese",
      render: () => <HobbiesStep employee={employee} onSaved={handleSaved} onSkip={next} />,
    },
    {
      key: "mentor", icon: GraduationCap, title: "Vrei să fii mentor?",
      render: () => (
        <div className="space-y-2">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">Înscrie-te ca mentor pe competențele pe care le stăpânești. (Opțional)</p>
          <MentorProfileEditor employee={employee} allSkills={allSkills || []} onSaved={handleSaved} onCancel={next} />
        </div>
      ),
    },
    {
      key: "mentee", icon: BookOpen, title: "Ce ai vrea să înveți?",
      render: () => (
        <div className="space-y-2">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">Adaugă competențele pe care vrei să le dezvolți — colegii mentori te vor putea ajuta. (Opțional)</p>
          <MenteeSkillsEditor employee={employee} allSkills={allSkills || []} onSaved={handleSaved} onCancel={next} />
        </div>
      ),
    },
    {
      key: "done", icon: PartyPopper, title: "Gata!",
      render: () => (
        <div data-testid="wizard-step-done" className="space-y-4 text-center py-2">
          <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
            <PartyPopper className="h-7 w-7" />
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Profil completat <strong className="text-zinc-900 dark:text-zinc-100">{completion.pct}%</strong>. Mulțumim! Îți poți edita oricând profilul din pagina ta.
          </p>
          <button data-testid="wizard-finish-btn" onClick={onClose} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90">
            <Check className="h-4 w-4" /> Închide
          </button>
        </div>
      ),
    },
  ];

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="profile-wizard" className="max-w-lg p-0 overflow-hidden bg-white dark:bg-zinc-900">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900">
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold tracking-tight">{current.title}</DialogTitle>
              <DialogDescription className="text-xs text-zinc-500 dark:text-zinc-400">
                Pasul {step + 1} din {STEPS.length} · profil {completion.pct}% complet
              </DialogDescription>
            </div>
          </div>
          <div className="mt-4 h-1.5 w-full rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
            <div data-testid="wizard-progress-bar" className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${completion.pct}%` }} />
          </div>
        </div>
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {current.render()}
        </div>
        {step > 0 && step < STEPS.length - 1 && (
          <div className="px-6 py-3 border-t border-border flex items-center justify-between">
            <button data-testid="wizard-back-btn" onClick={back} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100">
              <ArrowLeft className="h-4 w-4" /> Înapoi
            </button>
            <button data-testid="wizard-skip-all-btn" onClick={onClose} className="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">Închide wizard-ul</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ProfileCompletionBanner({ employee, onOpen, onDismiss }) {
  const { pct } = profileCompletion(employee);
  if (pct >= 100) return null;
  return (
    <div data-testid="profile-completion-banner" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
      <div className="flex items-center gap-4 p-4 rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/20">
        <span className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-white flex-shrink-0">
          <Rocket className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Profilul tău este {pct}% complet</p>
          <div className="mt-1.5 h-1.5 w-full max-w-xs rounded-full bg-emerald-100 dark:bg-emerald-900/40 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <button data-testid="banner-open-wizard-btn" onClick={onOpen} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex-shrink-0">
          Completează <ArrowRight className="h-4 w-4" />
        </button>
        <button data-testid="banner-dismiss-btn" onClick={onDismiss} aria-label="Închide" className="inline-flex items-center justify-center h-8 w-8 rounded-full text-zinc-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 flex-shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
